import React, { useState, useMemo, useCallback, useEffect, useContext } from 'react';
import styles from './ArenaReplayPanel.module.css';
import Board from '../Board';
import { GameContext } from '../Providers/GameContext';
import { GameRecordDocument } from '../../services/firebase/gameRecordService';
import { buildReplayGameState } from '../../services/zodiacEngine/Fen/FENDecoder';
import { inferMoveFromFENDelta } from '../../services/zodiacEngine/Fen/fenDeltaUtils';
import { isArenaVariantId, resolveVariantDescriptor, NAMED_VARIANTS } from '../../services/AIArena/AIArenaConstants';
import { SentinelEvaluator } from '../../services/zodiacEngine/AI/MinimaxAI/SentinelEvaluator';
import { ZodiacEngine } from '../../services/zodiacEngine/zodiacEngine';
import { loadTestScenarioFEN } from '../../services/zodiacEngine/Fen/FenLoader';
import { AIOrchestrator } from '../../services/zodiacEngine/AI/Core/AIOrchestrator';
import { AIPlayerType } from '../../services/zodiacEngine/AI/Core/IAIPlayer';
import {
  AIModel,
  AI_MODEL_CONFIG,
  TimeFrame,
  TIME_FRAME_CONFIG,
  getBaseAITypeFromModel,
} from '../../services/zodiacEngine/AI/Core/AIVariants';
import { ZodiacFEN } from '../../services/zodiacEngine/Fen/ZodiacFEN';
import { MoveType } from '../../services/zodiacEngine/types';
import { AIMoveResult } from '../../services/zodiacEngine/AI/Core/IAIPlayer';
import MoveExplanationModal from './MoveExplanationModal';
import {
  BlunderTestCase,
  saveTestCase,
  generateTestCaseId,
} from '../../services/AIArena/BlunderTestSuite';

interface StandaloneFen {
  fen: string;
  lightKing: string;
  darkKing: string;
}

interface ArenaReplayPanelProps {
  gameRecord: GameRecordDocument | null;
  standaloneFen?: StandaloneFen | null;
  onBlunderSaved?: () => void;
}

const winConditionLabels: Record<string, string> = {
  'race': 'Race',
  'kingslayer': 'Capture',
  'zombie-race': 'Zombie Race',
  'timeout': 'Timeout',
  'forfeit': 'Forfeit',
  'surrender': 'Surrender',
  'draw-stalemate': 'Stalemate',
  'draw-3-peat': '3-fold Rep.',
};

// Build variant list for the AI test dropdown (same pattern as AIArena.tsx)
const IMPLEMENTED_MODELS: AIModel[] = [
  AIModel.GAIA, AIModel.SENTINEL, AIModel.ALCHEMIST,
  AIModel.ZENITH, AIModel.HUNTER, AIModel.ARROW, AIModel.WISP, AIModel.CREATURE,
];

interface TestVariant {
  key: string;
  model: AIModel;
  timeFrame: TimeFrame;
  label: string;
  timeLimit: number;
}

const TEST_VARIANTS: TestVariant[] = [];
for (const model of IMPLEMENTED_MODELS) {
  for (const tf of Object.values(TimeFrame)) {
    const key = `${model}-${tf}`;
    const tfConfig = TIME_FRAME_CONFIG[tf];
    const label = NAMED_VARIANTS[key] || `${AI_MODEL_CONFIG[model].displayName} (${tfConfig.timeLimit / 1000}s)`;
    TEST_VARIANTS.push({ key, model, timeFrame: tf, label, timeLimit: tfConfig.timeLimit });
  }
}

interface TestMoveResult {
  description: string;
  confidence: number;
  thinkingTime: number;
  reasoning?: string;
  postFen: string;
  moveResult: AIMoveResult;
  isMinimaxModel: boolean;
  isPriorityModel: boolean;
  fromLabel: string;
  toLabel: string;
}

export default function ArenaReplayPanel({ gameRecord, standaloneFen, onBlunderSaved }: ArenaReplayPanelProps) {
  const { updateGameState } = useContext(GameContext);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [moveDescription, setMoveDescription] = useState<string>('');

  // AI test move state
  const [selectedVariantKey, setSelectedVariantKey] = useState<string>('sentinel-standard');
  const [testSide, setTestSide] = useState<'light' | 'dark'>('light');
  const [isThinking, setIsThinking] = useState(false);
  const [testMoveResult, setTestMoveResult] = useState<TestMoveResult | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);

  // Save/export state
  const [copyLabel, setCopyLabel] = useState('Save');

  // Blunder save form state
  const [showBlunderForm, setShowBlunderForm] = useState(false);
  const [blunderName, setBlunderName] = useState('');
  const [blunderDescription, setBlunderDescription] = useState('');
  const [blunderExpected, setBlunderExpected] = useState('');
  const [blunderSeverity, setBlunderSeverity] = useState<'critical' | 'major' | 'minor'>('major');

  const fenHistory = gameRecord?.fenHistory ?? [];
  const totalMoves = fenHistory.length;

  // Reset index and clear test move when a new game is loaded
  useEffect(() => {
    setCurrentIndex(0);
    setMoveDescription('');
    setTestMoveResult(null);
  }, [gameRecord]);

  // Clear test move when navigating
  const navigateTo = useCallback((newIndex: number) => {
    setTestMoveResult(null);
    setShowExplanation(false);
    setCurrentIndex((prev) => {
      const clamped = Math.max(0, Math.min(totalMoves - 1, newIndex));
      if (clamped > prev && clamped > 0 && clamped < totalMoves) {
        try {
          let desc = inferMoveFromFENDelta(fenHistory[clamped - 1], fenHistory[clamped]) || '';
          if (clamped === totalMoves - 1 && gameRecord?.winCondition && gameRecord.result !== 'draw') {
            const label = winConditionLabels[gameRecord.winCondition] || gameRecord.winCondition;
            const pieceMatch = desc.match(/^((?:Light|Dark)\s+\w+)/);
            const subject = pieceMatch ? pieceMatch[1] : (gameRecord.result === 'light' ? 'Light' : 'Dark');
            desc = `${desc}\n${subject} won by ${label}`;
          } else if (clamped === totalMoves - 1 && gameRecord?.result === 'draw') {
            const label = winConditionLabels[gameRecord.winCondition] || 'Draw';
            desc = `${desc}\nGame ended in ${label}`;
          }
          setMoveDescription(desc);
        } catch {
          setMoveDescription('');
        }
      } else {
        setMoveDescription('');
      }
      return clamped;
    });
  }, [totalMoves, fenHistory, gameRecord?.winCondition, gameRecord?.result]);

  // Build game state from test move FEN, standalone FEN, or replay FEN
  const activeFen = testMoveResult
    ? testMoveResult.postFen
    : standaloneFen
      ? standaloneFen.fen
      : (totalMoves > 0 ? fenHistory[currentIndex] : null);

  const activeKings = standaloneFen
    ? { light: standaloneFen.lightKing, dark: standaloneFen.darkKing }
    : gameRecord
      ? { light: gameRecord.lightKing, dark: gameRecord.darkKing }
      : null;

  const replayGameState = useMemo(() => {
    if (!activeFen || !activeKings) return null;
    return buildReplayGameState(
      activeFen,
      activeKings.light,
      activeKings.dark,
      currentIndex,
      true
    );
  }, [activeFen, activeKings, currentIndex]);

  // Compute eval on-the-fly from the current board position (works for ALL games)
  const evalScore = useMemo(() => {
    if (!replayGameState) return null;
    return SentinelEvaluator.getAbsoluteBoardValue(replayGameState);
  }, [replayGameState]);

  // Sync to GameContext so SquareRenderer sees Mode.Dev for crown rendering
  useEffect(() => {
    if (replayGameState) {
      updateGameState(replayGameState);
    }
  }, [replayGameState, updateGameState]);

  // AI Test Move handler
  const handleTestMove = useCallback(async () => {
    const currentFen = standaloneFen
      ? standaloneFen.fen
      : (totalMoves > 0 ? fenHistory[currentIndex] : null);
    if (!currentFen || isThinking) return;

    const variant = TEST_VARIANTS.find(v => v.key === selectedVariantKey);
    if (!variant) return;

    setIsThinking(true);
    setTestMoveResult(null);

    try {
      // Create a fresh engine and load the current FEN
      const engine = new ZodiacEngine();
      engine.setVerboseLogging(false);
      loadTestScenarioFEN(engine, currentFen);

      // FEN from game history uses encodeWithoutKings — king markers are stripped.
      // Restore isKing flag using the known king types from the game record.
      if (activeKings) {
        const realState = (engine as any).gameStateManager.getGameState();
        for (let row = 0; row < realState.board.length; row++) {
          for (let col = 0; col < realState.board[row].length; col++) {
            for (const piece of realState.board[row][col].Pieces) {
              if (piece.isLight && piece.type === activeKings.light) {
                piece.isKing = true;
                realState.lightKing = piece.type;
              } else if (!piece.isLight && piece.type === activeKings.dark) {
                piece.isKing = true;
                realState.darkKing = piece.type;
              }
            }
          }
        }
      }

      // Create orchestrator and configure the selected model
      const orchestrator = new AIOrchestrator(engine);
      const baseType = getBaseAITypeFromModel(variant.model);
      const isMinimax = baseType === AIPlayerType.MINIMAX;
      const isPriority = baseType === AIPlayerType.DIONS_AI || baseType === AIPlayerType.CREATURE;
      const gs = engine.getGameState();

      // Force the selected side to move
      const isLightTurn = testSide === 'light';
      gs.isLightTurn = isLightTurn;
      // CRITICAL: Also set on the real engine state — engine.getGameState() returns a shallow copy,
      // but the AI reads from the engine's internal state directly via gameStateManager
      (engine as any).gameStateManager.getGameState().isLightTurn = isLightTurn;

      if (isLightTurn) {
        orchestrator.setLightPlayer(baseType);
      } else {
        orchestrator.setDarkPlayer(baseType);
      }

      // Configure minimax model variant if applicable
      const config = AI_MODEL_CONFIG[variant.model];
      if (config.minimaxModel && baseType === AIPlayerType.MINIMAX) {
        const minimaxAI = orchestrator.getAI(AIPlayerType.MINIMAX) as any;
        if (minimaxAI?.setModel) {
          minimaxAI.setModel(config.minimaxModel);
        }
      }

      // Request the AI move
      const moveResult = await orchestrator.requestAIMove(gs, variant.timeLimit);

      if (!moveResult) {
        setTestMoveResult({
          description: 'AI returned no move',
          confidence: 0,
          thinkingTime: 0,
          postFen: currentFen,
          moveResult: null as any,
          isMinimaxModel: false,
          isPriorityModel: false,
          fromLabel: '',
          toLabel: '',
        });
        return;
      }

      // Build description for the first move
      const fromPos = moveResult.fromPosition;
      const toPos = moveResult.move.position;
      const colLetters = 'ABCDE';
      const fromLabel = `${colLetters[fromPos.col]}${fromPos.row + 1}`;
      const toLabel = `${colLetters[toPos.col]}${toPos.row + 1}`;
      const fromSquare = gs.board[fromPos.row]?.[fromPos.col];
      const actualPiece = fromSquare?.Pieces.find(p => p.id === moveResult.move.pieceId);
      const side = actualPiece ? (actualPiece.isLight ? 'Light' : 'Dark') : (isLightTurn ? 'Light' : 'Dark');
      const pieceName = moveResult.movingPiece?.type || actualPiece?.type || 'Unknown';
      const captured = moveResult.move.toPiece ? ` captures ${moveResult.move.toPiece.type}` : '';
      const moveDescs: string[] = [`${side} ${pieceName} ${fromLabel}\u2192${toLabel}${captured}`];

      // Execute the move on the engine to get post-move state
      const piece = gs.board[fromPos.row][fromPos.col].Pieces.find(
        p => p.id === moveResult.move.pieceId
      );
      if (piece) {
        await engine.makeMove(
          fromPos,
          toPos,
          piece,
          moveResult.move.toPiece,
          moveResult.move.moveType,
          moveResult.move.sideEffects
        );
      }

      // Continue executing bonus turns until the full turn is complete
      // (Virus infect/leave, Penguin slide+capture, Monkey swing/consume)
      const realEngineState = (engine as any).gameStateManager.getGameState();
      let bonusMoveCount = 0;
      const BONUS_MOVE_LIMIT = 10;

      while (realEngineState.skipTurnChange && realEngineState.activeBonusTurnPieceId && bonusMoveCount < BONUS_MOVE_LIMIT) {
        bonusMoveCount++;
        const bonusGs = engine.getGameState();
        bonusGs.isLightTurn = isLightTurn;
        (engine as any).gameStateManager.getGameState().isLightTurn = isLightTurn;

        const bonusResult = await orchestrator.requestAIMove(bonusGs, variant.timeLimit);
        if (!bonusResult) break;

        const bFrom = bonusResult.fromPosition;
        const bTo = bonusResult.move.position;
        const bFromLabel = `${colLetters[bFrom.col]}${bFrom.row + 1}`;
        const bToLabel = `${colLetters[bTo.col]}${bTo.row + 1}`;
        const bPiece = bonusGs.board[bFrom.row]?.[bFrom.col]?.Pieces.find(p => p.id === bonusResult.move.pieceId);
        const bPieceName = bonusResult.movingPiece?.type || bPiece?.type || '?';
        const bCaptured = bonusResult.move.toPiece ? ` captures ${bonusResult.move.toPiece.type}` : '';

        if (bPiece) {
          await engine.makeMove(bFrom, bTo, bPiece, bonusResult.move.toPiece, bonusResult.move.moveType, bonusResult.move.sideEffects);
        }

        if (bonusResult.move.moveType !== MoveType.endTurn) {
          moveDescs.push(`${bPieceName} ${bFromLabel}\u2192${bToLabel}${bCaptured}`);
        }
      }

      const postFen = ZodiacFEN.encode(engine.getGameState());

      setTestMoveResult({
        description: moveDescs.join(' \u2192 '),
        confidence: moveResult.confidence,
        thinkingTime: moveResult.thinkingTime,
        reasoning: moveResult.reasoning,
        postFen,
        moveResult,
        isMinimaxModel: isMinimax,
        isPriorityModel: isPriority,
        fromLabel,
        toLabel,
      });
    } catch (err) {
      setTestMoveResult({
        description: `Error: ${err instanceof Error ? err.message : 'Unknown error'}`,
        confidence: 0,
        thinkingTime: 0,
        postFen: currentFen,
        moveResult: null as any,
        isMinimaxModel: false,
        isPriorityModel: false,
        fromLabel: '',
        toLabel: '',
      });
    } finally {
      setIsThinking(false);
    }
  }, [standaloneFen, totalMoves, isThinking, selectedVariantKey, testSide, fenHistory, currentIndex]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!gameRecord || isThinking) return;
    switch (e.key) {
      case 'ArrowLeft':
        navigateTo(currentIndex - 1);
        e.preventDefault();
        break;
      case 'ArrowRight':
        navigateTo(currentIndex + 1);
        e.preventDefault();
        break;
      case 'Home':
        navigateTo(0);
        e.preventDefault();
        break;
      case 'End':
        navigateTo(totalMoves - 1);
        e.preventDefault();
        break;
    }
  }, [totalMoves, gameRecord, navigateTo, currentIndex, isThinking]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const noopDrop = () => {};
  const noopDragOver = () => {};

  // Save blunder handler
  const handleSaveBlunder = useCallback(() => {
    if (!testMoveResult || !activeKings) return;
    const preFen = standaloneFen
      ? standaloneFen.fen
      : (totalMoves > 0 ? fenHistory[currentIndex] : '');
    if (!preFen) return;

    const tc: BlunderTestCase = {
      id: generateTestCaseId(),
      name: blunderName || `blunder-${Date.now()}`,
      description: blunderDescription || testMoveResult.description,
      fen: preFen,
      lightKing: activeKings.light,
      darkKing: activeKings.dark,
      testSide,
      blunderMove: testMoveResult.fromLabel
        ? { from: testMoveResult.fromLabel, to: testMoveResult.toLabel }
        : undefined,
      expectedNote: blunderExpected,
      severity: blunderSeverity,
      sourceGameId: gameRecord?.id,
      sourceMoveIndex: currentIndex,
      dateCreated: new Date().toISOString(),
      tags: [],
    };
    saveTestCase(tc);
    setShowBlunderForm(false);
    setBlunderName('');
    setBlunderDescription('');
    setBlunderExpected('');
    setBlunderSeverity('major');
    onBlunderSaved?.();
  }, [testMoveResult, activeKings, standaloneFen, totalMoves, fenHistory, currentIndex, testSide, blunderName, blunderDescription, blunderExpected, blunderSeverity, gameRecord?.id, onBlunderSaved]);

  // Placeholder when no game and no standalone FEN loaded
  if (!gameRecord && !standaloneFen) {
    return (
      <div className={styles.container}>
        <div className={styles.placeholder}>
          <span className={styles.placeholderIcon}>&#x1F3AC;</span>
          <span>Click Replay on a game to load it here</span>
        </div>
      </div>
    );
  }

  // Standalone FEN mode (from test suite "Load Position")
  if (!gameRecord && standaloneFen) {
    return (
      <div className={styles.container}>
        <div className={styles.gameHeader}>
          <div className={styles.gameTitle}>Test Position</div>
          <div className={styles.gameSubtitle}>Loaded from blunder test suite</div>
        </div>
        <div className={styles.boardContainer}>
          {replayGameState && (
            <Board
              gameState={replayGameState}
              uiState={{ potentialMoves: [], potentialSideEffects: [] }}
              onDrop={noopDrop}
              onDragOver={noopDragOver}
            />
          )}
        </div>
        {/* AI Test Move Section */}
        <div className={styles.testMoveSection}>
          <span className={styles.testMoveLabel}>Test AI Move</span>
          <div className={styles.testMoveRow}>
            <div className={styles.sideToggle}>
              <button className={`${styles.sideButton} ${testSide === 'light' ? styles.sideButtonLight : ''}`} onClick={() => setTestSide('light')} disabled={isThinking}>Light</button>
              <button className={`${styles.sideButton} ${testSide === 'dark' ? styles.sideButtonDark : ''}`} onClick={() => setTestSide('dark')} disabled={isThinking}>Dark</button>
            </div>
            <select className={styles.variantSelect} value={selectedVariantKey} onChange={(e) => setSelectedVariantKey(e.target.value)} disabled={isThinking}>
              {TEST_VARIANTS.map(v => (<option key={v.key} value={v.key}>{v.label}</option>))}
            </select>
            <button className={styles.testMoveButton} onClick={handleTestMove} disabled={isThinking}>{isThinking ? 'Thinking...' : 'Test'}</button>
            <button className={styles.undoButton} onClick={() => setTestMoveResult(null)} disabled={!testMoveResult || isThinking}>Undo</button>
          </div>
          {isThinking && <span className={styles.thinkingIndicator}>AI is thinking...</span>}
          {testMoveResult && !isThinking && (
            <div className={styles.testMoveResult}>
              <span className={styles.testMoveResultPiece}>{testMoveResult.description}</span>
              {' \u2014 '}
              <span className={styles.testMoveResultConfidence}>{Math.round(testMoveResult.confidence * 100)}% confidence</span>
              {` \u2022 ${(testMoveResult.thinkingTime / 1000).toFixed(1)}s`}
              {(testMoveResult.isMinimaxModel || testMoveResult.isPriorityModel) && testMoveResult.moveResult && (
                <button className={styles.whyButton} onClick={() => setShowExplanation(true)}>Why?</button>
              )}
              {testMoveResult.reasoning && (
                <span className={styles.testMoveResultReasoning}>{testMoveResult.reasoning}</span>
              )}
            </div>
          )}
        </div>
        {showExplanation && testMoveResult?.moveResult && (
          <MoveExplanationModal
            moveResult={testMoveResult.moveResult}
            variantLabel={TEST_VARIANTS.find(v => v.key === selectedVariantKey)?.label || selectedVariantKey}
            moveDescription={testMoveResult.description}
            onClose={() => setShowExplanation(false)}
          />
        )}
      </div>
    );
  }

  // At this point, gameRecord is guaranteed non-null (standalone + no-game cases returned above)
  const gr = gameRecord!;
  const winnerName = gr.result === 'draw'
    ? 'Draw'
    : gr.result === 'light'
      ? gr.lightPlayerUsername
      : gr.darkPlayerUsername;
  const winLabel = gr.winCondition
    ? winConditionLabels[gr.winCondition] || gr.winCondition
    : '';

  function handleCopyGame() {
    const lines: string[] = [];
    lines.push(`Game: ${gr.lightPlayerUsername} (Light) vs ${gr.darkPlayerUsername} (Dark)`);
    lines.push(`Result: ${gr.result === 'draw' ? 'Draw' : gr.result === 'light' ? 'Light wins' : 'Dark wins'}${gr.winCondition ? ` (${gr.winCondition})` : ''}`);
    lines.push(`Turns: ${gr.turnCount}`);
    lines.push(`Light King: ${gr.lightKing} | Dark King: ${gr.darkKing}`);
    lines.push('');
    for (let i = 0; i < fenHistory.length; i++) {
      let delta = '';
      if (i > 0) {
        try { delta = inferMoveFromFENDelta(fenHistory[i - 1], fenHistory[i]) || ''; }
        catch { /* ignore */ }
      }
      lines.push(`[Move ${i}]${delta ? ' ' + delta : ''}`);
      lines.push(fenHistory[i]);
      lines.push('');
    }
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopyLabel('Copied!');
      setTimeout(() => setCopyLabel('Save'), 1500);
    }).catch(() => {});
  }

  return (
    <div className={styles.container}>
      {/* Game header */}
      <div className={styles.gameHeader}>
        <div className={styles.gameTitle}>
          {gr.lightPlayerUsername} vs {gr.darkPlayerUsername}
        </div>
        <div className={styles.gameSubtitle}>
          <span className={gr.result === 'light' ? styles.winnerLight : gr.result === 'dark' ? styles.winnerDark : ''}>
            {winnerName}
          </span>
          {winLabel && ` \u2022 ${winLabel}`}
          {` \u2022 ${gr.turnCount} moves`}
        </div>
      </div>

      {/* Dark player label */}
      <div className={styles.playerLabel}>
        <span className={`${styles.playerSide} ${styles.playerSideDark}`}>Dark</span>
        <span className={styles.playerName}>{gr.darkPlayerUsername}</span>
        {isArenaVariantId(gr.darkPlayerId) && (
          <span className={styles.playerDescriptor}>{resolveVariantDescriptor(gr.darkPlayerId)}</span>
        )}
      </div>

      {/* Board + Vertical Eval Bar */}
      <div className={styles.boardRow}>
        {/* Vertical eval bar (left of board) */}
        {(() => {
          if (evalScore === null) return null;
          const isTerminal = Math.abs(evalScore) >= 100000;
          // Chess-like scale: divide by 10 (10 raw = 1 piece)
          const chessEval = isTerminal ? (evalScore > 0 ? 99 : -99) : evalScore / 10;
          // Clamp to ±7.0 for bar percentage
          const clamped = Math.max(-7, Math.min(7, chessEval));
          // Map -7..+7 to 0..100% (50% = even). Light fills from bottom.
          const lightPct = 50 + (clamped / 7) * 50;
          const isLightAdv = evalScore > 0;
          const isEven = evalScore === 0;

          let displayText: string;
          if (isTerminal) {
            displayText = evalScore > 0 ? '+M' : '-M';
          } else {
            const abs = Math.abs(chessEval);
            displayText = `${chessEval >= 0 ? '+' : '-'}${abs.toFixed(1)}`;
          }

          return (
            <div className={styles.evalBarVertical}>
              <div className={styles.evalTrack}>
                <div
                  className={styles.evalFillLight}
                  style={{ height: `${lightPct}%` }}
                />
              </div>
              <span className={styles.evalNumber} style={{
                color: isEven ? '#94a3b8' : isLightAdv ? '#60a5fa' : '#f87171',
              }}>
                {displayText}
              </span>
            </div>
          );
        })()}

        {/* Board */}
        <div className={styles.boardContainer}>
          {replayGameState && (
            <Board
              gameState={replayGameState}
              uiState={{ potentialMoves: [], potentialSideEffects: [] }}
              onDrop={noopDrop}
              onDragOver={noopDragOver}
            />
          )}
        </div>
      </div>

      {/* Light player label */}
      <div className={styles.playerLabel}>
        <span className={`${styles.playerSide} ${styles.playerSideLight}`}>Light</span>
        <span className={styles.playerName}>{gr.lightPlayerUsername}</span>
        {isArenaVariantId(gr.lightPlayerId) && (
          <span className={styles.playerDescriptor}>{resolveVariantDescriptor(gr.lightPlayerId)}</span>
        )}
      </div>

      {/* Move description */}
      <div className={styles.moveDescription}>
        {moveDescription || '\u2014'}
      </div>

      {/* Navigation controls */}
      <div className={styles.controls}>
        <button
          className={styles.controlButton}
          onClick={() => navigateTo(0)}
          disabled={currentIndex === 0 || isThinking}
          title="Start (Home)"
        >
          &#x23EE;
        </button>
        <button
          className={styles.controlButton}
          onClick={() => navigateTo(currentIndex - 1)}
          disabled={currentIndex === 0 || isThinking}
          title="Back (Left Arrow)"
        >
          &#x23F4;
        </button>
        <button
          className={styles.controlButton}
          onClick={() => navigateTo(currentIndex + 1)}
          disabled={currentIndex >= totalMoves - 1 || isThinking}
          title="Forward (Right Arrow)"
        >
          &#x23F5;
        </button>
        <button
          className={styles.controlButton}
          onClick={() => navigateTo(totalMoves - 1)}
          disabled={currentIndex >= totalMoves - 1 || isThinking}
          title="End (End)"
        >
          &#x23ED;
        </button>
        <span className={styles.moveIndicator}>
          {currentIndex}/{totalMoves - 1}
        </span>
        <button
          className={styles.toggleButton}
          onClick={handleCopyGame}
          title="Copy all FENs and move deltas to clipboard"
        >
          {copyLabel}
        </button>
      </div>

      {/* Turn indicator */}
      <span className={styles.turnIndicator}>
        {replayGameState?.isLightTurn ? 'Dark' : 'Light'} to move
      </span>

      {/* AI Test Move Section */}
      <div className={styles.testMoveSection}>
        <span className={styles.testMoveLabel}>Test AI Move</span>
        <div className={styles.testMoveRow}>
          {/* Side toggle */}
          <div className={styles.sideToggle}>
            <button
              className={`${styles.sideButton} ${testSide === 'light' ? styles.sideButtonLight : ''}`}
              onClick={() => setTestSide('light')}
              disabled={isThinking}
            >
              Light
            </button>
            <button
              className={`${styles.sideButton} ${testSide === 'dark' ? styles.sideButtonDark : ''}`}
              onClick={() => setTestSide('dark')}
              disabled={isThinking}
            >
              Dark
            </button>
          </div>
          <select
            className={styles.variantSelect}
            value={selectedVariantKey}
            onChange={(e) => setSelectedVariantKey(e.target.value)}
            disabled={isThinking}
          >
            {TEST_VARIANTS.map(v => (
              <option key={v.key} value={v.key}>{v.label}</option>
            ))}
          </select>
          <button
            className={styles.testMoveButton}
            onClick={handleTestMove}
            disabled={isThinking || totalMoves === 0}
          >
            {isThinking ? 'Thinking...' : 'Test'}
          </button>
          <button
            className={styles.undoButton}
            onClick={() => setTestMoveResult(null)}
            disabled={!testMoveResult || isThinking}
          >
            Undo
          </button>
        </div>

        {/* Thinking indicator */}
        {isThinking && (
          <span className={styles.thinkingIndicator}>
            AI is thinking...
          </span>
        )}

        {/* Test move result */}
        {testMoveResult && !isThinking && (
          <div className={styles.testMoveResult}>
            <span className={styles.testMoveResultPiece}>{testMoveResult.description}</span>
            {' \u2014 '}
            <span className={styles.testMoveResultConfidence}>
              {Math.round(testMoveResult.confidence * 100)}% confidence
            </span>
            {` \u2022 ${(testMoveResult.thinkingTime / 1000).toFixed(1)}s`}
            {(testMoveResult.isMinimaxModel || testMoveResult.isPriorityModel) && testMoveResult.moveResult && (
              <button
                className={styles.whyButton}
                onClick={() => setShowExplanation(true)}
              >
                Why?
              </button>
            )}
            <button
              className={styles.saveBlunderButton}
              onClick={() => {
                setBlunderName(`blunder-${currentIndex}`);
                setBlunderDescription(testMoveResult.description);
                setShowBlunderForm(true);
              }}
            >
              Save Blunder
            </button>
            {testMoveResult.reasoning && (
              <span className={styles.testMoveResultReasoning}>
                {testMoveResult.reasoning}
              </span>
            )}
          </div>
        )}

        {/* Save Blunder Form */}
        {showBlunderForm && testMoveResult && (
          <div className={styles.blunderForm}>
            <div className={styles.blunderFormRow}>
              <input
                className={styles.blunderInput}
                placeholder="Name (e.g. crane-king-exposed)"
                value={blunderName}
                onChange={(e) => setBlunderName(e.target.value)}
              />
              <select
                className={styles.blunderSeveritySelect}
                value={blunderSeverity}
                onChange={(e) => setBlunderSeverity(e.target.value as any)}
              >
                <option value="critical">Critical</option>
                <option value="major">Major</option>
                <option value="minor">Minor</option>
              </select>
            </div>
            <input
              className={styles.blunderInput}
              placeholder="Description"
              value={blunderDescription}
              onChange={(e) => setBlunderDescription(e.target.value)}
            />
            <input
              className={styles.blunderInput}
              placeholder="What SHOULD the AI do?"
              value={blunderExpected}
              onChange={(e) => setBlunderExpected(e.target.value)}
            />
            <div className={styles.blunderFormRow}>
              <span className={styles.blunderFormInfo}>
                Blunder move: {testMoveResult.fromLabel}\u2192{testMoveResult.toLabel} &bull; {testSide} to move
              </span>
              <button className={styles.blunderSaveBtn} onClick={handleSaveBlunder}>Save</button>
              <button className={styles.blunderCancelBtn} onClick={() => setShowBlunderForm(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Move Explanation Modal */}
      {showExplanation && testMoveResult?.moveResult && (
        <MoveExplanationModal
          moveResult={testMoveResult.moveResult}
          variantLabel={TEST_VARIANTS.find(v => v.key === selectedVariantKey)?.label || selectedVariantKey}
          moveDescription={testMoveResult.description}
          onClose={() => setShowExplanation(false)}
        />
      )}
    </div>
  );
}
